import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaToggleOn,
  FaToggleOff,
  FaFolder,
  FaFolderOpen,
  FaTrash,
  FaSearch,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import moment from 'moment';

import Tabela from '~/Components/TabelaDinamica/Tabela';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  getTabelas,
  getCorrecoes,
  alterarStatusTabela,
  alterarDpcTabela,
  excluirTabela,
} from '~/Services/Service';

import ModalAlterarData from './ModalAlterarData';

const statusOpcoes = [
  { value: 'T', label: 'Todas' },
  { value: 'A', label: 'Ativas' },
  { value: 'I', label: 'Inativas' },
];

const selectSmStyles = {
  ...customStyles,
  control: (base) => ({
    ...customStyles.control(base),
    minHeight: 31,
    height: 31,
    padding: 0,
    fontSize: '0.875rem',
    borderRadius: '.2rem',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 6px',
    height: 29,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 29,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px',
  }),
  option: (base, state) => ({
    ...customStyles.option(base, state),
    fontSize: '0.875rem',
    padding: '6px 10px',
  }),
};

const TabTabelas = ({ familia }) => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFiltro, setStatusFiltro] = useState('A');
  const [correcaoFiltro, setCorrecaoFiltro] = useState({
    value: 0,
    label: 'Todas as correções',
  });
  const [correcoes, setCorrecoes] = useState([]);
  const [busca, setBusca] = useState('');
  const [correcaoSelecionada, setCorrecaoSelecionada] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [tabelaExcluir, setTabelaExcluir] = useState(null);
  const [showAlterarData, setShowAlterarData] = useState(false);
  const [tabelaSelecionada, setTabelaSelecionada] = useState(null);

  const temPermissao = AuthStorage.temCaracteristica(
    caracteristicas.ExcluirTabelaPreco
  );

  const carregarDados = useCallback(async () => {
    if (!familia || !correcaoSelecionada) return;
    setLoading(true);
    try {
      const correcaoValue = correcaoFiltro ? correcaoFiltro.value : null;
      const res = await getTabelas(familia, correcaoValue, statusFiltro);
      setDados(res || []);
    } catch {
      toast.error('Erro ao carregar tabelas');
    } finally {
      setLoading(false);
    }
  }, [familia, correcaoFiltro, statusFiltro, correcaoSelecionada]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    if (familia) {
      getCorrecoes(familia)
        .then((res) => setCorrecoes(res || []))
        .catch(() => {});
    }
  }, [familia]);

  const opcoesCorrecao = [
    { value: 0, label: 'Todas as correções' },
    ...correcoes.map((c) => ({
      value: c.codigo,
      label: `${c.codigo} - ${c.descricao}`,
    })),
  ];

  const handleCorrecaoChange = (opt) => {
    setCorrecaoFiltro(opt);
    setCorrecaoSelecionada(!!opt);
  };

  const handleAlterarData = (item) => {
    setTabelaSelecionada(item);
    setShowAlterarData(true);
  };

  const handleAlterarStatus = async (item) => {
    try {
      await alterarStatusTabela(item.codigo_tabela);
      toast.success('Status alterado com sucesso');
      carregarDados();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao alterar status';
      toast.error(msg);
    }
  };

  const handleToggleDpc = async (item) => {
    try {
      const novoDpc = item.desc_pasta_comp === 'S' ? 'N' : 'S';
      await alterarDpcTabela(item.codigo_tabela, { desc_pasta_comp: novoDpc });
      toast.success(
        novoDpc === 'S' ? 'Pasta completa ativada' : 'Pasta completa desativada'
      );
      carregarDados();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao alterar pasta completa';
      toast.error(msg);
    }
  };

  const handleExcluir = (item) => {
    setTabelaExcluir(item);
    setShowConfirm(true);
  };

  const confirmarExclusao = async (confirmado) => {
    setShowConfirm(false);
    if (!confirmado) return;
    try {
      await excluirTabela(tabelaExcluir.codigo_tabela);
      toast.success('Tabela excluída com sucesso');
      carregarDados();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data) ||
        'Erro ao excluir tabela';
      toast.error(msg);
    }
  };

  const dadosFiltrados = dados.filter((item) => {
    if (!busca) return true;
    const termo = busca.toLowerCase();
    return (
      (item.codigo_tabela && item.codigo_tabela.toString().includes(termo)) ||
      (item.descricao && item.descricao.toLowerCase().includes(termo))
    );
  });

  const colunas = [
    { title: 'Código', key: 'codigo_tabela', width: '80px' },
    { title: 'Descrição', key: 'descricao' },
    {
      title: 'Data',
      key: 'data',
      width: '100px',
      render: (v) => (v ? moment(v).format('DD/MM/YYYY') : ''),
    },
    {
      title: 'Status',
      key: 'status',
      width: '80px',
      render: (v) => (v === 'A' ? 'Ativa' : 'Inativa'),
    },
    {
      title: 'DPC',
      key: 'desc_pasta_comp',
      width: '60px',
      render: (v) => (v === 'S' ? 'Sim' : 'Não'),
    },
    {
      title: 'Data Geração',
      key: 'data_geracao',
      width: '120px',
      render: (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : ''),
    },
  ];

  const acoes = [
    ...(temPermissao
      ? [
          {
            label: '', title: 'Alterar Data',
            icon: <FaCalendarAlt />,
            onClick: handleAlterarData,
          },
          {
            label: '', title: 'Alterar Status',
            icon: <FaToggleOn />,
            onClick: handleAlterarStatus,
          },
        ]
      : []),
    {
      label: '', title: 'Pasta Completa',
      icon: <FaFolderOpen />,
      onClick: handleToggleDpc,
    },
    ...(temPermissao
      ? [
          {
            label: '', title: 'Excluir',
            icon: <FaTrash />,
            onClick: handleExcluir,
            type: 'danger',
          },
        ]
      : []),
  ];

  if (loading) return <Loading />;

  return (
    <>
      <Row className="mb-3 align-items-end">
        <Col xs={12} md={4} className="mb-2 mb-md-0">
          <Form.Label className="small mb-1">Correção</Form.Label>
          <Select
            value={correcaoFiltro}
            onChange={handleCorrecaoChange}
            options={opcoesCorrecao}
            placeholder="Selecione uma correção..."
            styles={selectSmStyles}
            components={{ DropdownIndicator }}
          />
        </Col>
        <Col xs={6} md={2} className="mb-2 mb-md-0">
          <Form.Label className="small mb-1">Status</Form.Label>
          <Select
            value={statusOpcoes.find((o) => o.value === statusFiltro)}
            onChange={(opt) => setStatusFiltro(opt ? opt.value : 'A')}
            options={statusOpcoes}
            styles={selectSmStyles}
            components={{ DropdownIndicator }}
          />
        </Col>
        <Col xs={6} md={3} className="mb-2 mb-md-0">
          <Form.Label className="small mb-1">Buscar</Form.Label>
          <div style={{ position: 'relative' }}>
            <Form.Control
              size="sm"
              type="text"
              placeholder="Código ou descrição..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            <FaSearch
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999',
                pointerEvents: 'none',
              }}
            />
          </div>
        </Col>
        {correcaoSelecionada && dados.length > 0 && (
          <Col xs={12} md={3} className="text-md-right text-right mt-2 mt-md-0 d-flex align-items-end justify-content-end">
            <small className="text-muted">
              {dadosFiltrados.length} de {dados.length} tabela(s)
            </small>
          </Col>
        )}
      </Row>

      {!correcaoSelecionada ? (
        <p className="text-muted text-center mt-4">
          Selecione uma correção para visualizar as tabelas de preço.
        </p>
      ) : (
        <Tabela colunas={colunas} dados={dadosFiltrados} acoes={acoes} action />
      )}

      <ModalAlterarData
        show={showAlterarData}
        onHide={() => setShowAlterarData(false)}
        tabela={tabelaSelecionada}
        onSalvar={() => {
          setShowAlterarData(false);
          carregarDados();
        }}
      />

      <ModalConfirmacao
        show={showConfirm}
        onClose={confirmarExclusao}
        title="Excluir Tabela"
        message="A exclusão removerá todos os dados relacionados (coeficientes, qualidades, preços por imóvel). Deseja continuar?"
      />
    </>
  );
};

export default TabTabelas;
